import { computed, Injectable, signal } from '@angular/core';
import {
  DEFAULT_CONNECTION_STATE,
  ORG_TYPES
} from '@cpd-app/core-module/constants/connect.const';
import {
  CurrentUserConnections,
  MatchedOrganizations,
  OrganizationList,
  PremiumPartnerConnectionInput,
  PremiumPartnerModel,
  UserConnectionProps
} from '@cpd-app/core-module/models';
import { ConnectApiService } from './connect-api.service';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { forkJoin, Observable, of, shareReplay } from 'rxjs';
import { AuthFacade } from '@cpd-app/state/auth';

@Injectable({
  providedIn: 'root'
})
export class ConnectDataService {
  private readonly connectionList$ = signal<OrganizationList>({
    academic: [],
    employer: [],
    nonprofit: []
  });
  private readonly matchedConnection$ = signal<MatchedOrganizations>({
    academic: [],
    employer: [],
    nonprofit: [],
    totalCount: 0
  });
  private readonly connectedState$ = signal<CurrentUserConnections>(
    DEFAULT_CONNECTION_STATE
  );
  private readonly premiumPartners$ = signal<PremiumPartnerModel[]>([]);
  private readonly isInitializedStore$ =
    this.authFacade.isSessionInitializing$.pipe(
      filter((isSessionInit) => !isSessionInit),
      shareReplay(1)
    );

  public readonly connectionList = computed(() => this.connectionList$());
  public readonly matchedConnections = computed<MatchedOrganizations>(() =>
    this.matchedConnection$()
  );
  public readonly connectedState = computed(() => this.connectedState$());
  public readonly premiumPartners = computed(() => this.premiumPartners$());

  public readonly isConnected = computed<boolean>(
    () =>
      this.connectedState$().academic.length > 0 ||
      this.connectedState$().nonprofit.length > 0 ||
      this.connectedState$().employer.length > 0 ||
      this.connectedState$().premiumPartnerIds.length > 0
  );

  public constructor(
    private connectApi: ConnectApiService,
    private authFacade: AuthFacade
  ) {}

  /**
   * The Connect panel's organisation lists and premium partners.
   *
   * CPD-671. Each call used to leave two subscriptions open on
   * `isInitializedStore$` for the life of the page, and that stream emits
   * every time a session finishes initialising, so every later
   * initialisation re-ran every call ever made -- the same queries twice on a
   * normal page load. `take(1)` makes a call one load.
   *
   * These lists are the same for every member, so they are fetched once per
   * page and kept; a failed fetch clears the flag so the next open retries.
   */
  private organizationsRequested = false;

  public getOrganization(): void {
    if (this.organizationsRequested) {
      return;
    }
    this.organizationsRequested = true;

    this.isInitializedStore$
      .pipe(
        take(1),
        switchMap(() => {
          return forkJoin(
            Object.values(ORG_TYPES).map((type) =>
              this.connectApi.getOrganization(type.toUpperCase())
            )
          );
        }),
        map(([academic, employer, nonprofit]) => ({
          academic,
          employer,
          nonprofit
        }))
      )
      .subscribe({
        next: (data) => {
          this.connectionList$.set(data);
        },
        error: () => {
          this.organizationsRequested = false;
        }
      });

    this.isInitializedStore$
      .pipe(
        take(1),
        switchMap(() => this.connectApi.getPremiumPartners())
      )
      .subscribe({
        next: (data) => {
          this.premiumPartners$.set(data);
        },
        error: () => {
          this.organizationsRequested = false;
        }
      });
  }

  /** One load per call; see `getOrganization` for why `take(1)`. */
  public getCurrentUserConnections(): Observable<void> {
    return this.isInitializedStore$.pipe(
      take(1),
      switchMap(() =>
        forkJoin([
          this.connectApi.getCurrentConnectionState(),
          this.connectApi.getMatchedOrganization()
        ])
      ),
      filter(
        ([currentUserConnection, matchedOrganizations]) =>
          !!currentUserConnection && !!matchedOrganizations
      ),
      map(([currentUserConnection, matchedOrganizations]) => {
        this.connectedState$.set(currentUserConnection);
        this.matchedConnection$.set(matchedOrganizations);
      })
    );
  }

  public updateConnectionState(
    connectionProps: UserConnectionProps
  ): Observable<void> {
    return this.connectApi.updateUserConnection(connectionProps).pipe(
      switchMap(() => this.connectApi.getCurrentConnectionState()),
      map((currentUserConnection) => {
        this.connectedState$.set(currentUserConnection);
      })
    );
  }

  /**
   * CONNECT.1. Request Info's save: add the schools, then reload what the
   * member is connected to, so the school table's "Info requested" markers and
   * the Connect panel agree without a page reload.
   *
   * A failed RELOAD is not a failed request. The schools were saved; the
   * markers will appear on the next session load. Telling the member "That did
   * not send" would make them send again for nothing.
   */
  public requestInfoFromSchools(
    schoolIds: number[],
    confirmAdult = false
  ): Observable<void> {
    return this.connectApi.requestInfoFromSchools(schoolIds, confirmAdult).pipe(
      switchMap(() =>
        this.connectApi.getCurrentConnectionState().pipe(
          map((currentUserConnection) => {
            if (currentUserConnection) {
              this.connectedState$.set(currentUserConnection);
            }
          }),
          catchError(() => of(undefined))
        )
      )
    );
  }

  public updatePremiumConnection(
    premiumConnectionProps: PremiumPartnerConnectionInput
  ): Observable<void> {
    return this.connectApi
      .connectWithPremiumPartner(premiumConnectionProps)
      .pipe(
        switchMap(() => this.connectApi.getCurrentConnectionState()),
        map((currentUserConnection) => {
          this.connectedState$.set(currentUserConnection);
        })
      );
  }

  public revokePremiumConnection(premiumPartnerId: string): Observable<void> {
    return this.connectApi
      .revokePremiumPartnerConnection(premiumPartnerId)
      .pipe(
        switchMap(() => this.connectApi.getCurrentConnectionState()),
        map((currentUserConnection) => {
          this.connectedState$.set(currentUserConnection);
        })
      );
  }

  public acknowledgeOutdated(): Observable<void> {
    return this.connectApi.acknowledgeOutdatedConnection().pipe(
      switchMap(() => this.connectApi.getCurrentConnectionState()),
      map((currentUserConnection) => {
        this.connectedState$.set(currentUserConnection);
      })
    );
  }
}
